import React, { useState, useEffect } from "react";
import { Modal, Input, Button, message, Select } from "antd";
import { LoadingOutlined, VideoCameraOutlined, ClockCircleOutlined, DesktopOutlined, AppstoreOutlined } from "@ant-design/icons";
import axios from "axios";
import ENVIRONMENT from "../../../../environments";
import "./GenerateAiVideoModal.scss";
import SubscriptionUpgradeManager from "../SubscriptionUpgradeManager/SubscriptionUpgradeManager";
const { TextArea } = Input;

const INFLUENCER_PROMPT_WORD_LIMIT = 23;

const GenerateAiVideoModal = ({ visible, onCancel, imageUrl, image_id, onSuccess = () => {} }) => {
    const [prompt, setPrompt] = useState("");
    const [generateVideoLoader, setGenerateVideoLoader] = useState(false);
    const [requestStatus, setRequestStatus] = useState('update_subscription');
    const [subscriptionUpgradeManagerModel, setSubscriptionUpgradeManagerModel] = useState(false);
    const [cardStatus, setCardStatus] = useState(0);
    const [resolution, setResolution] = useState('720p');
    const [duration, setDuration] = useState(4);
    const [model, setModel] = useState('veo3');
    const [videoModeType, setVideoModeType] = useState('custom');

    // Call API every time modal opens
    useEffect(() => {
        if (visible) {
            loadUserRequestStatus();
        }

        // Reset prompt, resolution, duration, and model when modal closes
        if (!visible) {
            setPrompt("");
            setResolution('720p');
            setDuration(4);
            setModel('veo3');
            setVideoModeType('custom');
        }
    }, [visible]);

    const INFLUENCER_MODELS = ['veo3', 'seedance'];

    const handleModeTypeChange = (value) => {
        setVideoModeType(value);
        if (value === 'influencer') {
            const nextModel = INFLUENCER_MODELS.includes(model) ? model : 'veo3';
            setModel(nextModel);
            setDuration(getDurationOptions(nextModel)[0].value);
            setPrompt('');
        } else if (value === 'custom') {
            setPrompt('');
        } else if (value === 'panorama_360') {
            setPrompt('Generate 360 spin video of the product.');
        }
    };

    const getDurationOptions = (m) => {
        if (m === 'runway') return [{ value: 5, label: '5s' }, { value: 10, label: '10s' }];
        if (m === 'seedance') return [
            { value: 4, label: '4s' },
            { value: 6, label: '6s' },
            { value: 8, label: '8s' },
            { value: 10, label: '10s' },
            { value: 15, label: '15s' },
        ];
        return [{ value: 4, label: '4s' }, { value: 6, label: '6s' }, { value: 8, label: '8s' }];
    };

    const INFLUENCER_WORD_LIMIT_BY_MODEL = {
        veo3: { maxDurationSeconds: 8, maxWords: 23 },
        runway: { maxDurationSeconds: 10, maxWords: 23 },
        seedance: { maxDurationSeconds: 15, maxWords: 50 },
    };

    const countWords = (text) => (text.trim() === '' ? 0 : text.trim().split(/\s+/).length);
    const { maxDurationSeconds, maxWords } = INFLUENCER_WORD_LIMIT_BY_MODEL[model] || INFLUENCER_WORD_LIMIT_BY_MODEL.veo3;
    const influencerPromptWordLimit = Math.max(1, Math.ceil((Math.min(Math.max(duration, 1), maxDurationSeconds) / maxDurationSeconds) * maxWords));
    const promptWordCount = countWords(prompt);
    const isInfluencerPromptOverLimit = videoModeType === 'influencer' && promptWordCount > influencerPromptWordLimit;

    const handlePromptChange = (e) => {
        const newValue = e.target.value;
        if (videoModeType === 'influencer' && countWords(newValue) > influencerPromptWordLimit) return;
        setPrompt(newValue);
    };

    const promptPlaceholder =
        videoModeType === 'influencer'
            ? 'Write the script your influencer will speak in the video...'
            : videoModeType === 'panorama_360'
                ? 'Describe the 360° spin...'
                : 'Describe your video for this image...';

    const loadUserRequestStatus = () => {
        let payload = {
            "username": localStorage.getItem('username'),
            "request_type": "ai_video",
            "action": "check",
            "category": ""
        }
        axios.post(ENVIRONMENT.PAYMENT_GATEWAY, payload).then(res => {
            console.log('Video request status:', res.data)
            setRequestStatus(res.data['request_allowed']);
            setSubscriptionUpgradeManagerModel(res.data['subscription_upgrade_model']);
            setCardStatus(res.data['card_status']);
        });
    };


    const handleGenerate = () => {
        setGenerateVideoLoader(true);

        if(requestStatus == 'update_subscription') {
            setSubscriptionUpgradeManagerModel(true);
            setGenerateVideoLoader(false);
            return;
        }

        let request_payload = {
            action: 'trigger_video_generation_job',
            image_id: image_id,
            prompt_text: prompt,
            username: localStorage.getItem('username'),
            resolution: resolution,
            duration: duration,
            model: model,
            video_mode_type: videoModeType,
        }
        if(localStorage.getItem('company_id')) {
            request_payload.company_id = localStorage.getItem('company_id');
        }

        let gateway_payload = {
            "username": localStorage.getItem('username'),
            "request_type": "ai_video",
            "action": "perform",
            "request_payload": request_payload,
            "category": "",
            "request_id": Date.now().toString()
        }
        axios.post(ENVIRONMENT.PAYMENT_GATEWAY, gateway_payload).then(async (res) => {
            console.log(res);
            if(res.data.status == 1 && res.data.action_response.status == 200) {
                try {
                    await onSuccess();
                } catch (error) {
                    console.error("Error in onSuccess callback:", error);
                }
                setGenerateVideoLoader(false);
                onCancel();
                message.success('Video generation request submitted successfully');
            } else {
                setGenerateVideoLoader(false);
                message.error('Failed to submit video generation request');
            }
        });
    };

    return (
        <>  
        <Modal
            visible={visible}
            onCancel={onCancel}
            className="generate-ai-video-modal"
            centered
            width={600}
            footer={null}
        >
            <div style={{marginTop: 20}}>
                <span className="manrope f-20 black-00 w-500">Generate Video</span>
                <div className="modal-content">
                    <img src={imageUrl} alt="Source" className="img-for-video" />
                    <TextArea
                        placeholder={promptPlaceholder}
                        value={prompt}
                        onChange={handlePromptChange}
                        autoSize={{ minRows: 4, maxRows: 6 }}
                        className="prompt-textarea"
                    />
                    {videoModeType === 'influencer' && (
                        <span style={{ alignSelf: 'flex-end', fontSize: 12, color: isInfluencerPromptOverLimit ? '#ff4d4f' : '#8c8c8c' }}>
                            {promptWordCount}/{influencerPromptWordLimit} words
                        </span>
                    )}
                </div>
                    <div className="video-controls-row">
                        <div className="control-pill select-pill">
                            <AppstoreOutlined className="pill-icon" />
                            <Select
                                value={videoModeType}
                                onChange={handleModeTypeChange}
                                className="pill-select"
                                bordered={false}
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: 'custom', label: 'Custom Video' },
                                    { value: 'panorama_360', label: '360° Spin' },
                                    { value: 'influencer', label: 'Influencer Video' },
                                ]}
                            />
                        </div>
                        <div className="control-pill select-pill">
                            <VideoCameraOutlined className="pill-icon" />
                            <Select
                                value={model}
                                onChange={(value) => {
                                    setModel(value);
                                    setDuration(getDurationOptions(value)[0].value);
                                }}
                                className="pill-select"
                                bordered={false}
                                dropdownMatchSelectWidth={false}
                                options={videoModeType === 'influencer'
                                    ? [
                                        { value: 'veo3', label: 'Veo 3' },
                                        { value: 'seedance', label: 'Seedance' },
                                    ]
                                    : [
                                        { value: 'veo3', label: 'Veo 3' },
                                        { value: 'runway', label: 'Gen 4.5' },
                                        { value: 'seedance', label: 'Seedance' },
                                    ]
                                }
                            />
                        </div>
                        <div className="control-pill select-pill">
                            <ClockCircleOutlined className="pill-icon" />
                            <Select
                                value={duration}
                                onChange={(value) => setDuration(value)}
                                className="pill-select"
                                bordered={false}
                                dropdownMatchSelectWidth={false}
                                options={getDurationOptions(model)}
                            />
                        </div>
                        <div className="control-pill select-pill">
                            <DesktopOutlined className="pill-icon" />
                            <Select
                                value={resolution}
                                onChange={(value) => setResolution(value)}
                                className="pill-select"
                                bordered={false}
                                dropdownMatchSelectWidth={false}
                                options={[
                                    { value: '720p', label: '720p' },
                                    { value: '1080p', label: '1080p' },
                                ]}
                            />
                        </div>
                    </div>
                <div style={{display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 10}}>
                    {!prompt.trim() && !generateVideoLoader && (
                        <span className="manrope f-12" style={{ color: '#8c8c8c' }}>Enter prompt to continue</span>
                    )}
                    <Button key="cancel" onClick={onCancel} className="modal-okay-gray square font-14">
                        Cancel
                    </Button>
                    <Button
                        key="generate"
                        type="primary"
                        onClick={handleGenerate}
                        className="generate-ai-btn font-14"
                        disabled={!prompt.trim() || generateVideoLoader || isInfluencerPromptOverLimit}
                    >
                        {generateVideoLoader ? <LoadingOutlined /> : <img
                            src="/img/ai_creator/ai-twinkle.svg"
                            style={{ width: "20px", height: "20px", marginRight: "8px" }}
                        />}
                        Generate
                    </Button>
                </div>
            </div>
        </Modal>
        <SubscriptionUpgradeManager
            subscriptionUpgradeManagerModel={subscriptionUpgradeManagerModel}
            setSubscriptionUpgradeManagerModel={setSubscriptionUpgradeManagerModel}
            cardStatus={cardStatus}
            loadUserRequestStatus={loadUserRequestStatus}
            requestType={'ai_video'}
        />
        </>
    );
};

export default GenerateAiVideoModal;
